/**
 * Entry point: wires pointer input to the rules and the renderer.
 *
 * Input model
 *   - Drag the background      -> turn the pyramid. Releasing coasts; a flick
 *                                 pages to the next face and settles there.
 *   - Drag a piece off the tray -> it lifts out of the tray and floats over the
 *                                 board, easing onto whichever face it is over.
 *
 * Placing is drag-only, so the pyramid itself never takes a tap and turning it
 * can never drop a piece by accident. There is no rotation control: pieces have
 * one fixed orientation.
 *
 * Both gestures are pointer events, so mouse and touch are one code path. The
 * keyboard does nothing but close the rules with `Esc`.
 */

import { CELLS, CELL_COUNT, cellCentroid } from './geometry.js';
import {
  createState,
  deadSlots,
  formationsCompletedBy,
  placePiece,
  previewPlacement,
} from './game.js';
import { PIECES_BY_KEY, realizeOnSurface } from './pieces.js';
import { GameAudio } from './audio.js';
import { PyramidView } from './render.js';
import { loadHighScore, loadMuted, recordScore, saveMuted } from './storage.js';
import { lockTouchGestures } from './touch.js';
import { UI, describeResult } from './ui.js';
import { FIREWORK_COMBO } from './vfx.js';

const TAP_SLOP = 8; // px of travel before a press counts as a drag
const ORBIT_SPEED = 0.009;
/**
 * Horizontal travel in one pointer event before the turn clicks. Small — the
 * point is to skip the sub-pixel jitter of a held-still finger and the events a
 * vertical tilt generates, not to space the clicks out; the audio layer does
 * that, and doing it here as well would make the rate depend on how fast the
 * browser happens to be delivering moves.
 */
const TURN_TICK_PX = 1.5;
/**
 * How far above the pointer the piece is carried, so the whole of it stays in
 * view instead of hiding under the cursor or a fingertip. Touch needs more room:
 * a finger covers far more than a cursor does.
 *
 * This one number is also where the piece *aims*. It has to be: the piece is
 * what you are pointing with, so the cells that light up are the ones it is
 * sitting over. Aiming from the pointer instead puts the highlight a piece's
 * height below the piece, and you end up watching a triangle light up next to
 * the one you are holding your piece over.
 */
const CARRY_LIFT = 78;
const TOUCH_CARRY_LIFT = 112;
/**
 * How far a drop may be nudged to find a placement the piece fits, as a
 * multiple of a cell's on-screen size. Measured in screen space, and against a
 * cell measured live, so it means the same thing at any viewport or tilt.
 */
const SNAP_CELLS = 1.15;

/* --------------------------------------------------------- scoring sequence */

/**
 * Beat between one formation's score being shown and the next.
 *
 * One well-placed stone can close a dozen formations at once, and a fixed beat
 * would turn that into a ten-second cutscene. The whole announcement is
 * budgeted instead: a single clear gets the full beat, and a big combo trades
 * per-formation time to keep the total in hand.
 */
const SCORE_BUDGET_MS = 2600;
const SCORE_STEP_MIN_MS = 190;
const SCORE_STEP_MAX_MS = 520;
const scoreStep = (count) =>
  Math.min(SCORE_STEP_MAX_MS, Math.max(SCORE_STEP_MIN_MS, SCORE_BUDGET_MS / count));

/** How long the landed piece's border glow is held before scoring starts. */
const PLACE_GLOW_MS = 340;
/** How long the last formation's glow lingers before the stones go. */
const SCORE_HOLD_MS = 420;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const canvas = document.getElementById('stage');
const tray = document.getElementById('tray');

const view = new PyramidView(canvas);
const ui = new UI(document);
const audio = new GameAudio();

// Pinch and double-tap zoom, before any of them can fire. The CSS covers panning
// and text selection; this is the part it cannot reach. See `touch.js`.
lockTouchGestures();

// Browsers will not start an AudioContext outside a user gesture, and one built
// too early lands suspended and stays there. Every gesture tries; the first one
// that is allowed wins, and the rest are no-ops.
for (const event of ['pointerdown', 'keydown']) {
  window.addEventListener(event, () => audio.resume(), { capture: true });
}

let state = createState();

/**
 * 'home' until Play is pressed. On the home screen the board keeps turning
 * behind the panel, but nothing is dealt and no gesture reaches the game.
 */
let phase = 'home';

/** High score, read once at startup and written when it is beaten. */
let best = loadHighScore();

// Applied before the graph is ever built, so a player who left the sound off does
// not get one gesture's worth of noise back on the next visit.
audio.setMuted(loadMuted());

/** Tray slot being dragged right now, or null. */
let heldSlot = null;

/** Current pointer gesture, if any. */
let gesture = null;

/**
 * Cells the drop highlight is on, as a string, so `aimHeldPiece` can tell a
 * pointer move that changed the target from the many that did not.
 */
let hoverKey = '';

/**
 * True while a move is being announced. The rules have already run by then, so
 * taking another piece would place it against a board the player has not been
 * shown yet. Turning the pyramid stays allowed — the glows ride with it.
 */
let busy = false;

/** Board-space point the last aim resolved against — exposed for tests. */
let lastAim = null;

/** The most recent move's result — exposed for tests. */
let lastResult = null;

function refreshTray() {
  const dead = deadSlots(state);
  ui.renderTray(state, heldSlot, dead);
  // The stones themselves are drawn in the canvas, over the (invisible) slots —
  // which also means hiding the tray in CSS is not enough to hide them. On the
  // home screen there is nothing in hand to draw.
  const entries = phase === 'playing' ? state.tray : [];
  view.syncTray(entries, heldSlot, ui.slotRects(canvas));
}

function syncBoard() {
  view.syncFilled(state.filled, state.tones);
  ui.setScore(state.score);
  refreshTray();
}

/**
 * Where the piece is — which is both where it is drawn and where it aims.
 * Screen coordinates, lifted clear of the pointer.
 */
function aimAt(event) {
  const lift = event.pointerType === 'touch' ? TOUCH_CARRY_LIFT : CARRY_LIFT;
  return { x: event.clientX, y: event.clientY - lift };
}

/**
 * Which placement the held piece is aiming at, or null.
 *
 * Ranked **on screen**, against where the piece is actually drawn. That is the
 * whole trick: you aim by looking, so the placement that lights up has to be
 * the one the piece appears to be sitting on. Ranking by distance in board
 * space instead looks wrong on a face turned away from the camera — that face
 * is foreshortened, so equal distances across it are nothing like equal
 * distances on screen, and the highlight jumps to a neighbour of the triangle
 * the piece is plainly over.
 *
 * The piece's *centre* is what points, not a corner of it: candidates are
 * ranked by how close the middle of the shape they would fill comes to the
 * middle of the piece.
 *
 * Every face turned toward the camera will take a piece — mid-turn that is
 * both of the faces you can see, not just the one squared up front. Only the
 * faces pointing away are off limits: aiming at a face you cannot see is not
 * aiming, so turn the pyramid to reach them. Near a seam the screen-space
 * ranking already keeps the choice honest — the winner is whichever face's
 * placement the piece is actually drawn over.
 *
 * Within a face, plenty of positions cannot host a given piece: it has to
 * fit on one face and can only sit on cells of its own orientation. Rather
 * than going dead the aim snaps to the nearest placement that works.
 *
 * @param {{x: number, y: number}} at where the piece is drawn, in screen pixels
 * @returns {{anchor: number, cells: number[]} | null}
 */
function resolveTarget(at) {
  if (!at || heldSlot === null) return null;

  const visible = view.visibleFaces;

  // Every legal placement on every visible face, and the middle of each.
  const candidates = [];
  const centres = [];
  for (const cell of CELLS) {
    if (!visible[cell.face]) continue;
    const cells = previewPlacement(state, heldSlot, cell.id);
    if (!cells) continue;

    const centre = [0, 0, 0];
    for (const id of cells) {
      const c = cellCentroid(id);
      for (let k = 0; k < 3; k += 1) centre[k] += c[k] / cells.length;
    }
    candidates.push({ anchor: cell.id, cells });
    centres.push(centre);
  }
  if (!candidates.length) return null;

  const screen = view.projectBoardPoints(centres);
  const limit = SNAP_CELLS * view.frontCellScreenSize();

  let best = null;
  let bestDistance = limit;
  screen.forEach((p, i) => {
    const d = Math.hypot(p.x - at.x, p.y - at.y);
    if (d < bestDistance) {
      bestDistance = d;
      best = candidates[i];
    }
  });

  lastAim = { ...at, gap: bestDistance };
  return best;
}

/** Update the drop highlight and steer the floating piece. */
function aimHeldPiece(aim) {
  if (heldSlot === null || state.over) {
    view.setPreview(null);
    hoverKey = '';
    return null;
  }

  const target = resolveTarget(aim);

  // Light up any formation the drop would complete, so the targets are legible.
  const armed = target
    ? formationsCompletedBy(state.filled, target.cells).flatMap((f) => f.cells)
    : null;

  // A tick each time the highlight lands somewhere new — so the snap is audible
  // as well as visible, which is what makes aiming feel positive on a face turned
  // away from the camera. Keyed on the cells, not on the pointer: most moves
  // leave the target where it was, and those are silent.
  const key = target ? target.cells.join(',') : '';
  if (key !== hoverKey) {
    hoverKey = key;
    if (key) audio.play('hover');
  }

  view.setPreview(target?.cells ?? null, armed, state.tray[heldSlot]?.tone ?? 0);
  view.setGhostTarget(aim);
  return target;
}

/**
 * Announce a move: the piece settles, its outline glows, then each completed
 * formation lights up in turn with its score, and only then do the stones go.
 *
 * Scoring is presentation only — the rules have already run, so `state` is
 * final throughout. The view keeps drawing the cleared stones until the last
 * formation has had its moment.
 */
async function present(result, placedTone, previousTones) {
  // Keep the cleared stones on screen: the rules removed them already, but the
  // player has not been shown why yet.
  const heldTones = new Map();
  for (const id of result.cleared) {
    heldTones.set(id, result.placed.includes(id) ? placedTone : previousTones.get(id) ?? 0);
  }
  view.holdStones(heldTones);

  // The points for placing land straight away; each formation adds its own as
  // it is announced. Working back from the final score keeps this honest — the
  // only thing still owed is what the formations are worth.
  const base = result.clears.reduce((sum, f) => sum + f.score, 0);
  let shown = state.score - Math.round(base * result.multiplier);
  ui.setScore(shown);

  // The piece flies out of the hand and settles onto the face it was aimed at.
  await view.dropHeldPiece(result.anchor, result.placed);
  audio.play('place', { cells: result.placed.length });
  syncBoard();

  // It gets its own moment before any scoring starts, so a quiet move still
  // reads as a move.
  view.addGlow(result.placed);
  await wait(PLACE_GLOW_MS);
  view.clearGlows();

  // Then each completed formation in turn: outline, score, beat. One glow at a
  // time — left to pile up, a combo ends as a single blur of light with no way
  // to tell which shape earned what.
  const step = scoreStep(Math.max(1, result.clears.length));
  for (const [index, formation] of result.clears.entries()) {
    view.clearGlows();
    view.addGlow(formation.cells, { kind: 'score' });

    // And a beam out of the tip for it, thrown harder the bigger the shape and
    // the longer the combo — so the same announcement that counts the score off
    // one formation at a time also builds the light over the pyramid.
    view.fireApexBeam({ cells: formation.cells.length, combo: result.clears.length });

    // `index` climbs the pentatonic ladder, so a combo rises as it is counted off.
    audio.play('clear', { step: index, cells: formation.cells.length });

    const points = Math.round(formation.score * result.multiplier);
    shown += points;
    ui.setScore(shown);
    ui.scorePopover(
      { label: formation.label, points, multiplier: result.multiplier },
      view.projectCells(formation.cells),
      step,
    );
    await wait(step);
  }

  // Only now do the stones go — and a move that closed several formations at
  // once gets fireworks over the wreckage as they do.
  if (result.clears.length) {
    await wait(SCORE_HOLD_MS);
    view.celebrateCombo([...result.cleared], result.clears.length);
    audio.play('combo', { count: result.clears.length });
    audio.play('shatter', { count: result.cleared.length });
    // One shell per formation, matching the salvo `celebrateCombo` just launched —
    // same count, same threshold, same stagger. Each shell carries its own launch
    // hiss and places its bang a rocket's climb later.
    if (result.clears.length >= FIREWORK_COMBO) {
      audio.play('fireworks', { count: result.clears.length });
    }
    view.clearGlows();
    view.releaseStones();
  }
}

/**
 * Place the held piece, then hand the move to `present` to be announced.
 *
 * Deciding and mutating happens synchronously so the caller gets a straight
 * yes or no; only the announcement is asynchronous.
 */
function commitAt(aim) {
  if (busy || heldSlot === null || state.over) return false;
  const target = resolveTarget(aim);
  if (!target) return false;

  // Tones as they were, so cleared stones can be drawn in their own colour
  // through the sequence — the rules have already forgotten them.
  const previousTones = new Map(state.tones);
  const placedTone = state.tray[heldSlot].tone ?? 0;

  const result = placePiece(state, heldSlot, target.anchor);
  if (!result) return false;
  lastResult = result;

  busy = true;
  heldSlot = null;
  view.setPreview(null);
  if (result.combo > 1) ui.toast(describeResult(result));
  refreshTray();

  present({ ...result, anchor: target.anchor }, placedTone, previousTones)
    .catch((error) => {
      // The announcement must never wedge the game: on any failure put the
      // board into its true state and give control back.
      console.error(error);
      view.clearGlows();
      view.releaseStones();
    })
    .finally(() => {
      busy = false;
      view.setHeldPiece(null);
      // True up: per-formation rounding need not sum to the exact total.
      ui.setScore(state.score);
      syncBoard();
      if (result.over) finish();
    });

  return true;
}

/** End of game: bank the score, then show how it went. */
function finish() {
  const outcome = recordScore(state.score);
  // A run that set a record gets its fanfare blooming out of the walk-down —
  // the audio layer places it, so this only has to say which ending it is.
  audio.play('gameOver', { record: outcome.record });
  best = outcome.best;
  ui.setBest(best);
  ui.showGameOver(state.score, best, outcome.record);
}

/** Leave the home screen and deal a fresh board. */
function startGame() {
  audio.resume();
  audio.play('start');
  state = createState();
  heldSlot = null;
  gesture = null;
  busy = false;
  phase = 'playing';

  view.resetPresentation();
  ui.clearPopovers();
  ui.hideHowTo();
  ui.hideGameOver();
  ui.resetScore();
  ui.setPhase('playing');
  ui.setBest(best);
  syncBoard();
}

/** Put a half-dragged piece back in the tray. */
function releaseHeld() {
  // Only a piece that was actually in hand makes a sound going back.
  if (heldSlot !== null) audio.play('return');
  heldSlot = null;
  view.setPreview(null);
  view.setHeldPiece(null);
  refreshTray();
}

/* ------------------------------------------------------------------ gestures */

tray.addEventListener('pointerdown', (event) => {
  const slotEl = event.target.closest('.tray-slot');
  if (!slotEl || phase !== 'playing' || state.over || busy) return;
  const slot = Number(slotEl.dataset.slot);
  const entry = state.tray[slot];
  if (!entry) return;

  event.preventDefault();

  // The piece leaves the tray and takes to the air straight away.
  heldSlot = slot;
  audio.play('lift');
  refreshTray();
  view.setHeldPiece(PIECES_BY_KEY.get(entry.key), entry.tone);
  const aim = aimAt(event);
  aimHeldPiece(aim);

  gesture = {
    kind: 'place',
    id: event.pointerId,
    moved: false,
    travel: 0,
    x: event.clientX,
    y: event.clientY,
  };

  try {
    slotEl.setPointerCapture(event.pointerId);
  } catch {
    /* not fatal — the drag still tracks on window */
  }
});

canvas.addEventListener('pointerdown', (event) => {
  if (phase !== 'playing' || state.over) return;
  event.preventDefault();
  view.beginOrbit();
  audio.play('grab');
  gesture = {
    kind: 'orbit',
    id: event.pointerId,
    moved: false,
    travel: 0,
    x: event.clientX,
    y: event.clientY,
  };
  try {
    canvas.setPointerCapture(event.pointerId);
  } catch {
    /* not fatal — the drag still tracks on window */
  }
});

window.addEventListener('pointermove', (event) => {
  if (!gesture || event.pointerId !== gesture.id) return;

  const dx = event.clientX - gesture.x;
  const dy = event.clientY - gesture.y;
  // Accumulate: an orbit consumes its delta each event, so comparing against
  // the gesture's start would never cross the threshold.
  gesture.travel += Math.hypot(dx, dy);
  if (gesture.travel > TAP_SLOP) gesture.moved = true;

  if (gesture.kind === 'orbit') {
    view.orbitBy(dx * ORBIT_SPEED, dy * ORBIT_SPEED * 0.55);
    // The board ticking past under the hand, pitched by which way it is going.
    // Gated on real horizontal travel so a vertical tilt is silent, and throttled
    // again in the audio layer — one click per pointer event would be a buzz.
    if (Math.abs(dx) >= TURN_TICK_PX) audio.play('turn', { dir: Math.sign(dx) });
    gesture.x = event.clientX;
    gesture.y = event.clientY;
    return;
  }

  const aim = aimAt(event);
  aimHeldPiece(aim);
});

window.addEventListener('pointerup', (event) => {
  if (!gesture || event.pointerId !== gesture.id) return;
  const { kind, moved } = gesture;
  gesture = null;

  if (kind === 'orbit') {
    view.endOrbit();
    audio.play('release');
    return;
  }

  // Only a real drag places anything; a tap just puts the piece back.
  if (moved) {
    const aim = aimAt(event);
    if (commitAt(aim)) return;
  }
  releaseHeld();
});

window.addEventListener('pointercancel', () => {
  if (gesture?.kind === 'orbit') {
    view.endOrbit();
    audio.play('release');
  }
  gesture = null;
  releaseHeld();
});

/* -------------------------------------------------------------- keyboard aid */

// Escape closes the rules, and that is the whole keyboard surface. Turning the
// pyramid is a drag: it used to be bound to the arrow keys as well, but a discrete
// jump to the next face is a different gesture from turning it by hand, and two ways
// to do one thing meant two things to explain and one of them always felt wrong.
window.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && ui.howToOpen) ui.hideHowTo();
});

/* -------------------------------------------------------------------- screens */

ui.playEl.addEventListener('click', startGame);
ui.restartEl.addEventListener('click', startGame);

ui.soundEl?.addEventListener('click', () => {
  const muted = !audio.muted;
  // `resume` first: this is a user gesture, and if the sound has been off since
  // load then this click is the first chance the context has had to start at all.
  audio.resume();
  audio.setMuted(muted);
  saveMuted(muted);
  ui.setMuted(muted);
  // Turning it on gets a click back, so the button proves itself. Turning it off
  // cannot, which is its own confirmation.
  if (!muted) audio.play('click');
});

ui.helpEl.addEventListener('click', () => {
  audio.play('click');
  ui.showHowTo();
});
// The title screen's own "How to play" link is gone — the `?` is on that screen
// too now, and one way in is enough. Optional so the binding survives either way.
ui.homeHelpEl?.addEventListener('click', () => ui.showHowTo());
ui.howToCloseEl.addEventListener('click', () => {
  audio.play('click');
  ui.hideHowTo();
});

// Clicking off the panel closes it, the usual way out of a modal.
ui.howToEl.addEventListener('pointerdown', (event) => {
  if (event.target === ui.howToEl) ui.hideHowTo();
});

/* ------------------------------------------------------------------ lifecycle */

window.addEventListener('resize', () => {
  view.resize();
  refreshTray();
});

/** Debug handle, also used by the smoke test. */
window.__luxor = {
  get state() {
    return state;
  },
  view,
  faceOf: (id) => CELLS[id].face,
  cellOf: (id) => CELLS[id],
  get previewSize() {
    return view.preview.size;
  },
  centroid: cellCentroid,
  /** Cells the held piece would fill anchored here, for checking the aim. */
  placementAt: (id) => previewPlacement(state, heldSlot, id),
  pieceOf: (key) => PIECES_BY_KEY.get(key),
  placementForKey: (key, anchor) => realizeOnSurface(anchor, PIECES_BY_KEY.get(key)),
  /** Where a dropped piece comes to rest — the transform the drop eases into. */
  seatOf: (anchor) => view.seatedTransform(anchor),
  /** The board-space point the last aim resolved against. */
  get lastAim() {
    return lastAim;
  },
  isBusy: () => busy,
  /** True once the GPU device is up and the particle system exists. */
  get ready() {
    return !!view.vfx;
  },
  /** Live particle/instance counts — how the smoke test sees the effects. */
  vfxStats: () => view.vfx?.stats() ?? null,
  audio,
  /** The most recent move's result, for checking how it was announced. */
  get lastResult() {
    return lastResult;
  },
  /** Redraw the tray after the harness has rewritten what is in it. */
  refreshTray,
  /** Fill the board so the game-over path can be exercised. */
  forceGameOver() {
    for (let id = 0; id < CELL_COUNT; id += 1) state.filled.add(id);
    state.over = true;
    syncBoard();
    finish();
    return true;
  },
  get phase() {
    return phase;
  },
  get highScore() {
    return best;
  },
  startGame,
};

function frame(now) {
  view.render(now);
  requestAnimationFrame(frame);
}

// The home screen shows first. The board is dealt but nothing is drawn in the
// tray and no gesture reaches it until Play is pressed.
ui.setPhase('home');
ui.setBest(best);
ui.setMuted(audio.muted);
syncBoard();

// WebGPU hands back its device asynchronously, and both the reflection probe
// and the particle system are built against it, so the first frame has to wait
// on that. Everything above is DOM and rules work that does not.
await view.init();
// A stone catching the light gets a chime. The audio layer throttles these —
// every filled cell has its own sparkle clock, so at a full board this fires far
// too often to play them all.
view.vfx.onSparkle = () => audio.play('tinkle');
requestAnimationFrame(frame);

// `?tune` puts sliders on the stonework's dimensions. Loaded on demand so the
// panel is not in the bundle everyone else downloads.
if (new URLSearchParams(location.search).has('tune')) {
  const { mountTuner } = await import('./tune.js');
  mountTuner(view);
}
